//CustomFormPreview.tsx
import React from "react";
import {
    Box,
    Typography,
    Grid,
    TextField,
    Paper,
    MenuItem,
    FormControlLabel,
    FormLabel,
    Switch,
    IconButton,
    Button,
    Chip,
    Autocomplete,
    Divider,
    Select,
    FormControl,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
    Undo,
    Redo,
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    StrikethroughS,
    FormatListBulleted,
    FormatListNumbered,
    FormatColorText,
    FormatColorFill,
    Link as LinkIcon,
    Image as ImageIcon,
    FormatClear,
    MoreHoriz,
} from "@mui/icons-material";
import dayjs from "dayjs";
import AddIcon from "@mui/icons-material/Add";
import { FormData, appActivity } from "../../../data/WorkFlowEditorType";

interface Props {
    type: string;
    name?: string;
    description?: string;
    formItems: FormData[];
    appActivityList?: appActivity[];
}

/**
 * 申請書フォームのプレビューコンポーネント
 * - 基本情報・フォームアイテム・アクティビティ（承認者）・参照者を実際の申請書レイアウトで表示する
 * - props: type（申請種別名）, name（申請書名）, description（詳細）, formItems（フォームアイテム一覧）, appActivityList（アクティビティ一覧）
 */
const CustomFormPreview: React.FC<Props> = ({
    type,
    name,
    description,
    formItems,
    appActivityList,
}) => {
    // フォームアイテムをセクション単位でグループ化する
    const groupedItems = formItems.reduce((groups, item) => {
        if (item.type === "section") {
            groups.push({ sectionName: item.label, items: [] });
        } else {
            if (groups.length === 0) {
                groups.push({ sectionName: undefined, items: [] });
            }
            groups[groups.length - 1].items.push(item);
        }
        return groups;
    }, [] as { sectionName?: string; items: FormData[] }[]);

    /**
     * フォームアイテムのtypeに応じたUIコンポーネントを返す
     * @param item - 描画対象のフォームアイテム
     * @returns フォームアイテムのtype（text, select, date など）に対応したMUIコンポーネント
     */
    const renderField = (item: FormData) => {
        switch (item.type) {
            case "text":
            case "textLong":
                return (
                    <TextField
                        label={item.label}
                        value={item.defaultValue ?? ""}
                        required={item.isRequired}
                        fullWidth
                        multiline={item.type === "textLong"}
                        rows={item.type === "textLong" ? 4 : 1}
                    />
                );
            // case "autocomplete": // 未実装のため一時的に非表示
            //     return (
            //         <TextField
            //             label={item.label}
            //             value={item.defaultValue ?? ""}
            //             required={item.isRequired}
            //             fullWidth
            //         />
            //     );
            case "select":
                const isValidSelectValue = item.selectItemList?.some(
                    (select) => select.value === item.defaultValue
                );
                return (
                    <TextField
                        select
                        label={item.label}
                        value={item.defaultValue ?? ""}
                        required={item.isRequired}
                        fullWidth
                        error={!isValidSelectValue && !!item.defaultValue} 
                        helperText={
                            !isValidSelectValue && !!item.defaultValue
                                ? "選択肢に存在しない値です"
                                : ""
                        }
                    >
                        {item.selectItemList?.sort((a, b) => a.selectOrder - b.selectOrder).map((select) => (
                            <MenuItem key={select.id} value={select.value}>
                                {select.value}
                            </MenuItem>
                        ))}
                    </TextField>
                );
            case "date": {
                const dateValue = item.defaultValue ? dayjs(item.defaultValue) : null;
                const isValidDate = dateValue?.isValid() ?? true;

                return (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label={item.label}
                            value={isValidDate ? dateValue : null}
                            format="YYYY/MM/DD"
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    required: item.isRequired,
                                    error: !isValidDate,
                                    helperText: !isValidDate ? "日付の形式が正しくありません" : "",
                                    InputProps: {
                                        readOnly: true, 
                                    },
                                },
                            }}
                        />
                    </LocalizationProvider>
                );
            }
            case "switch":
                return (
                    <FormControlLabel
                        control={<Switch checked={!!item.defaultValue} />}
                        label={item.label}
                        required={item.isRequired}
                    />
                );
            case "fileUpload":
                return (
                    <FormControl sx={{ width: "100%" }}>
                        <FormLabel sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {item.label}
                            {item.isRequired && <Box component="span" >*</Box>}
                        </FormLabel>

                        <Box
                            sx={{
                                mt: 1,
                                p: 2,
                                border: "2px dashed #3f51b5",
                                borderRadius: "8px",
                                textAlign: "center",
                                backgroundColor: "#f5f5f5",
                                cursor: "not-allowed",
                                width: "100%",
                            }}
                        >
                            <Typography variant="h6">
                                ファイルをドラッグ＆ドロップ、またはクリックしてファイルを選択
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<CloudUploadIcon />}
                                sx={{ mt: 2 }}
                            //   disabled
                            >
                                ファイルを選択
                            </Button>
                        </Box>
                    </FormControl>
                );
            case "linkList":
                return (
                    <FormControl sx={{ width: "100%" }}>
                        <FormLabel sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {item.label}
                            {item.isRequired && <Box component="span" >*</Box>}
                        </FormLabel>
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
                                <IconButton color="primary" size="large">
                                    <AddIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </FormControl>
                );

            case "richText":
                return (
                    <Box sx={{ width: "100%" }}>
                        {/* ツールバー（プレビュー用） */}
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.25,
                                alignItems: "center",
                                p: 0.5,
                                bgcolor: "#fafafa",
                                border: 1,
                                borderColor: "rgba(0, 0, 0, 0.23)",
                                borderRadius: "4px 4px 0 0",
                            }}
                        >
                            {/* 元に戻す/やり直す */}
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <Undo fontSize="small" />
                            </IconButton>
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <Redo fontSize="small" />
                            </IconButton>
                            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

                            {/* 段落/見出し */}
                            <Select
                                size="small"
                                value="paragraph"
                                sx={{ minWidth: 120, height: 32, fontSize: "0.8rem" }}
                            >
                                <MenuItem value="paragraph">標準テキスト</MenuItem>
                            </Select>

                            {/* フォントサイズ */}
                            <Select
                                size="small"
                                displayEmpty
                                value=""
                                sx={{ minWidth: 72, height: 32, fontSize: "0.8rem", ml: 0.5 }}
                                renderValue={() => "サイズ"}
                            >
                                <MenuItem value="">デフォルト</MenuItem>
                            </Select>
                            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

                            {/* 文字装飾 */}
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <FormatBold fontSize="small" />
                            </IconButton>
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <FormatItalic fontSize="small" />
                            </IconButton>
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <FormatUnderlined fontSize="small" />
                            </IconButton>
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <StrikethroughS fontSize="small" />
                            </IconButton>
                            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

                            {/* テキストカラー・ハイライト */}
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <FormatColorText fontSize="small" />
                            </IconButton>
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <FormatColorFill fontSize="small" />
                            </IconButton>
                            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

                            {/* リスト */}
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <FormatListBulleted fontSize="small" />
                            </IconButton>
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <FormatListNumbered fontSize="small" />
                            </IconButton>
                            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

                            {/* リンク・画像 */}
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <LinkIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <ImageIcon fontSize="small" />
                            </IconButton>
                            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

                            {/* 書式クリア */}
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <FormatClear fontSize="small" />
                            </IconButton>
                            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

                            {/* その他のツール */}
                            <IconButton size="small" sx={{ borderRadius: 1 }}>
                                <MoreHoriz fontSize="small" />
                            </IconButton>
                        </Box>

                        {/* エディタ枠（プレビュー用） */}
                        <Box
                            sx={{
                                border: 1,
                                borderColor: "rgba(0, 0, 0, 0.23)",
                                borderTop: 0,
                                borderRadius: "0 0 4px 4px",
                                p: 1,
                                minHeight: 240,
                            }}
                        >
                            <Typography sx={{ color: "#9e9e9e", lineHeight: 1.6 }}>
                                {item.label}
                            </Typography>
                        </Box>
                    </Box>
                );

            case "applicationNumber":
                const numbers = item.defaultValue ? item.defaultValue.split(",") : [];
                return (
                    <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={numbers}
                        readOnly
                        renderTags={(value: string[], getTagProps) =>
                            value.map((option: string, index: number) => (
                                <Chip
                                    variant="outlined"
                                    label={
                                        <span style={{ color: "#1976d2", textDecoration: "underline" }}>
                                            {option}
                                        </span>
                                    }
                                    {...getTagProps({ index })}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={item.label}
                                placeholder="申請番号"
                                required={item.isRequired}
                            />
                        )}
                        disabled
                    />
                );
            default:
                return (
                    <TextField
                        label={item.label}
                        value={item.defaultValue}
                        required={item.isRequired}
                        fullWidth
                        disabled
                    />
                );
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Paper sx={{ p: 4, maxWidth: "900px", margin: "0 auto" }}>
                <Grid container spacing={3}>
                    {/* 基本情報 */}
                    <Grid size={12}>
                        <Typography variant="h2">{type}</Typography>
                    </Grid>
                    <Grid size={12}>
                        <Typography variant="h3">{name}</Typography>
                    </Grid>
                    <Grid size={12}>
                        <Typography variant="h3" sx={{ fontWeight: "normal" }}>
                            {description}
                        </Typography>
                    </Grid>

                    {/* 申請者 */}
                    <Grid size={12}>
                        <TextField
                            label="申請者"
                            fullWidth
                            required
                            disabled
                            sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                                },
                            }}
                        />
                    </Grid>

                    {/* フォームアイテム */}
                    {groupedItems.map((group, idx) => (
                        <Grid size={12} key={idx}>
                            {group.sectionName && (
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    {group.sectionName}
                                </Typography>
                            )}
                            <Grid container spacing={2}>
                                {group.items.map((item) => (
                                    <Grid size={12} key={item.formId}>
                                        {renderField(item)}
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    ))}

                    {/* アクティビティ（承認者） */}
                    {appActivityList
                        ?.filter((activity) => activity.template?.code === "1") 
                        .map((activity, idx) => (
                            <Grid size={12} key={idx}>
                                <Typography variant="h6">{activity.appActivityName}</Typography>
                                <TextField
                                    label="承認者"
                                    fullWidth
                                    sx={{ mt: 1 }}
                                />
                            </Grid>
                        ))}


                    {/* 参照者 */}
                    <Grid size={12}>
                        <TextField
                            label="参照者"
                            fullWidth
                            disabled
                        />
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default CustomFormPreview;
